import React from "react";
import { generateClassName } from "../../../hooks/useAttributes";
import useRoutes from "../../../hooks/useRoutes";
import useWindowSize from "../../../hooks/useWindowSize";
import { IAppPath } from "../../../routes/AppRoutes";
import Icon from "../icons/Icon";
import { IElementPosition, ISideNavTooltip, SideNavIconSize } from "./SideNav";
import "./SideNavLink.css";

interface ISideNavLinkProps {
  path: IAppPath,
  otherValidPaths?: IAppPath[],
  sideBarIsExpanded: boolean,
  visible: boolean,
  setHighlightPosition: (pos: IElementPosition) => void,
  toggleTooltip: (t: ISideNavTooltip | null) => void,
  closeSidebar: () => void
}

export default function SideNavLink({ path, otherValidPaths, closeSidebar, toggleTooltip, sideBarIsExpanded, setHighlightPosition, visible }: ISideNavLinkProps) {

  const {
    isActiveRoute,
    currentRoute,
    navigate
  } = useRoutes();

  const {
    Icon: icon,
    IconSrc
  } = path;

  const { width } = useWindowSize();

  const [active, setActive] = React.useState<boolean>(false);
  const currentRef = React.useRef<HTMLDivElement>(null);

  const setActiveState = (val: boolean) => {
    if (val === active) return;
    setActive(val);
    if (val) moveHighlightToCurrent();
  }

  React.useEffect(() => {
    if (!active) return () => { }
    const x = setTimeout(() => moveHighlightToCurrent(), 100);
    return () => clearTimeout(x);
  }, [sideBarIsExpanded, width, currentRef, active]);

  React.useEffect(() => {
    if (!path) return setActiveState(false);

    const isActiveLink = isActiveRoute(path.Route);

    if (isActiveLink) return setActiveState(true);

    if (!otherValidPaths || !otherValidPaths.length) return setActiveState(false);

    const otherValidPathsMatch = otherValidPaths.reduce((prev, curr) => {
      if (prev) return true;
      return isActiveRoute(curr.Route);
    }, false);

    setActiveState(otherValidPathsMatch);

  }, [currentRoute]);

  const getPosition = (): IElementPosition => {
    const defaultData: IElementPosition = { height: 0, left: 0, top: 0, width: 0 };

    if (!currentRef?.current) return defaultData;

    const clientRect = currentRef.current.getBoundingClientRect();

    if (!clientRect) return defaultData;

    return clientRect;
  }

  const moveHighlightToCurrent = () => setHighlightPosition(getPosition());


  const clickHandler = () => {
    moveHighlightToCurrent();
    navigate(path.Route);
    closeSidebar();
  }

  const containerClass = generateClassName("sidenav-link d-flex flex-row align-items-center", {
    value: active,
    onTrue: "sidenav-link-active"
  }, {
    value: sideBarIsExpanded,
    onTrue: "sidenav-link-sidebar-expanded"
  });

  const toggleTooltipFromData = (show: boolean) => {
    if (sideBarIsExpanded || !show || !path || !path.Label || !path.Description) {
      toggleTooltip(null);
      return;
    }

    toggleTooltip({ position: getPosition(), title: path.Label, tooltip: path.Description });
  }

  if (!path) return null;
  if (!visible) return null;

  return (
    <div
      className={containerClass}
      onClick={clickHandler}
      ref={currentRef}
      onMouseEnter={() => toggleTooltipFromData(true)}
      onMouseLeave={() => toggleTooltipFromData(false)}
      onMouseOver={() => toggleTooltipFromData(true)}
    >
      <div className="d-flex flex-row align-items-center justify-content-center" style={{ minWidth: "var(--sideNavWidth)" }}>
        {
          icon
            ? (
              <Icon icon={icon} size={SideNavIconSize} />
            )
            : (
              IconSrc
              && (
                <img
                  className="side-nav-link-img"
                  width={SideNavIconSize}
                  src={`/assets/${IconSrc}`}
                  style={{
                    width: SideNavIconSize,
                  }}
                />
              )
            )
        }
      </div>
      <div className={generateClassName("sidenav-link-text d-none flex-column overflow-hidden", {
        value: sideBarIsExpanded,
        onTrue: "sidenav-link-text-expanded"
      })}>
        <p className="text-uppercase sidenav-link-label m-0 p-0 fs-6 font-code fw-bold text-nowrap">{path.Label}</p>
        {
          path.Description && <span className="sidenav-link-description font-s text-truncate m-0 p-0">{path.Description}</span>
        }
      </div>
    </div>
  )
}